import React from "react";
import UseCaseTemplate from "./UseCaseTemplate";
import booking1 from "./assets/booking/booking_case1.png";
import booking2 from "./assets/booking/booking_case2.png";
import booking3 from "./assets/booking/booking_case3.png";
import booking4 from "./assets/booking/booking_case4.png";

const experiences = [
  {
    id: "living-room-tour",
    number: "01",
    category: "Verified room quality",
    title: "The “Living Room Tour”",
    lead: "Turn operational room checks into an immersive, time-stamped media layer directly from a smartphone.",
    problem: "Outdated photos and misleading wide-angle shots drive post-stay guest complaints and chargebacks.",
    layout: "Storyboard",
    layoutDetail: "Strict sequence of 4 to 6 slots, 8 seconds each",
    workflowTitle: "Verified room checkpoints",
    workflow: [
      "Real window view.",
      "Bedding and bedside outlets.",
      "Bathroom and shower water pressure.",
      "Acoustic insulation check.",
      "On-site housekeeping or operations staff recapture these checkpoints monthly; the embedded weave-player can display an active “Updated 4 days ago” trust badge.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Lower refund rates and increased booking conversion for premium inventory.",
    outcome2Label: "Operational value",
    outcome2: "Automatically transform staff inspections into verified video tours and sync updated room features to listings without heavy production.",
    image: booking1,
  },
  {
    id: "neighborhood-vibe",
    number: "02",
    category: "Surrounding ecosystem",
    title: "The “Neighborhood Vibe”",
    lead: "Sell the neighborhood, not just the room, through authentic local lifestyle proof.",
    problem: "Static maps and generic location bullets do not show the real transit access, street life, nearby businesses or acoustic environment around a stay.",
    layout: "Relay / Living feed",
    layoutDetail: "Geolocated micro-video proof from the surrounding ecosystem",
    workflowTitle: "Local proof around the property",
    workflow: [
      "Capture transit access and the walking experience around the hotel.",
      "Show nearby cafés, bakeries and local points of interest through verified micro-videos.",
      "Use real-world soundscapes to reduce uncertainty about location and noise.",
      "Keep the neighborhood feed time-stamped and updateable on the booking page.",
    ],
    outcome1Label: "Booking impact",
    outcome1: "A more immersive local context can lift listing engagement and reduce location doubt before checkout.",
    outcome2Label: "Guest confidence",
    outcome2: "Potential guests see the authentic local lifestyle before booking instead of relying only on static maps.",
    image: booking2,
  },
  {
    id: "dynamic-seasonality",
    number: "03",
    category: "Mountain resorts",
    title: "Dynamic Seasonality: Summer vs. Winter",
    lead: "Show the property in the season that matches the traveler’s target stay instead of mixing contradictory imagery.",
    problem: "Resorts struggle with off-season sales because fixed imagery is often unaligned with current weather or the traveler’s dates.",
    layout: "Versus",
    layoutDetail: "Synchronized 50/50 split-screen",
    workflowTitle: "Temporal mirror",
    workflow: [
      "Winter side: steaming outdoor hot tub in snow and ski-in/ski-out access.",
      "Summer side: heated outdoor pool, downhill mountain biking and hiking trails.",
      "Booking dynamically flips the featured side based on the traveler’s target check-in dates.",
      "The permanent property URL remains unchanged.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Optimized occupancy across transitional shoulder seasons.",
    outcome2Label: "Conversion value",
    outcome2: "365-day visual relevance and an interactive dual-season player can keep guests engaged with the property’s year-round value.",
    image: booking3,
  },
  {
    id: "verified-video-reviews",
    number: "04",
    category: "UGC bounties",
    title: "Verified Video Reviews via Smart Escrow",
    lead: "Turn guest-generated footage into a controlled, brand-safe stream of current social proof.",
    problem: "User-generated guest videos are often poorly framed, erratic or legally unusable.",
    layout: "Seed",
    layoutDetail: "Prompt-driven paid missions",
    workflowTitle: "Verified in-stay missions",
    workflow: [
      "Launch a targeted three-slot bounty for active guests, such as “Capture the 8:30 AM breakfast buffet”.",
      "AI Content Guard screens submissions for face blurring, sharpness and subject accuracy.",
      "Validated clips release €5 to €15 in Booking credits from the hotel escrow wallet.",
      "Certified in-stay video proof can then appear on the booking experience.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Scalable production of verified, brand-safe video proof without dedicated film crews.",
    outcome2Label: "Weaver monetization",
    outcome2: "Verified in-stay guests receive automated micro-rewards for approved contributions.",
    image: booking4,
  },
];

export default function BookingPage() {
  return (
    <UseCaseTemplate
      pageTitle={"Booking"}
      currentPath={"/use-cases/booking"}
      eyebrow={"WEAVEVO USE CASE · BOOKING"}
      heroTitle={"Turn hotel listings into verified living media."}
      heroText={"Replace static property galleries with time-stamped room proof, neighborhood context, seasonal reality and verified guest contributions that can evolve without breaking the listing URL."}
      heroAlt={"Booking use case showing a live hotel listing connected to mobile Weavevo media"}
      finalEyebrow={"BUILD THE NEXT BOOKING WEAVE"}
      finalTitle={"A booking page should reflect the stay guests can expect now."}
      finalText={"Use living room tours, neighborhood proof, seasonal views and verified in-stay video to keep hotel inventory current and trustworthy."}
      heroImage={booking1}
      experiences={experiences}
    />
  );
}
